import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Checkbox, Tooltip } from 'antd';
import { CloseCircleFilled } from '@ant-design/icons';
import { Dropdown } from '@/components/AntdCompatDropdown';
import { ChevronDownSmallIcon } from '@/assets/icons/icons';
import TeamTagPill from '../../TeamsTagPill/TeamTagPill';
import EllipsisTooltip from '../../EllipsisTooltip';
import SearchInput from '../../SearchInput';
import '../../style/TeamDropdownSelect.css';

export interface PreviewDropdownOptions {
  id: string;
  name: string;
  color?: string;
  permissions?: {
    is_create?: boolean;
    is_update?: boolean;
  };
  disabled?: boolean;
  is_default_admin?: boolean;
  [key: string]: unknown;
}

interface PreviewDropdownProps {
  options: PreviewDropdownOptions[];
  selectedOptions: PreviewDropdownOptions[];
  onChange: (teams: PreviewDropdownOptions[]) => void;
  singleSelect?: boolean;
  formState?: string;
  hideChevronWhenDisabled?: boolean;
  disabled?: boolean;
  onSearch?: (value: string) => void;
  optionsSearch?: string;
  showSearch?: boolean;
  label?: React.ReactNode;
  className?: string;
  onPopupScroll?: (e: React.UIEvent<HTMLDivElement>) => void;
  loading?: boolean;
  searchPlaceholder?: string;
  renderOption?: (
    team: PreviewDropdownOptions,
    isSelected: boolean
  ) => React.ReactNode;
  renderTrigger?: (
    selectedOptions: PreviewDropdownOptions[]
  ) => React.ReactNode;
  showClearIcon?: boolean;
  /** Class applied to the antd popup wrapper, for scoping menu-only overrides (the popup is portaled, so caller-scoped descendant selectors can't reach it otherwise). */
  overlayClassName?: string;
}

const PreviewDropdown: React.FC<PreviewDropdownProps> = ({
  options,
  selectedOptions,
  onChange,
  singleSelect = false,
  formState = '',
  hideChevronWhenDisabled = false,
  disabled = false,
  onSearch,
  optionsSearch = '',
  showSearch,
  label,
  className = '',
  onPopupScroll,
  loading = false,
  searchPlaceholder,
  renderOption,
  renderTrigger,
  showClearIcon = true,
  overlayClassName,
}) => {
  const [open, setOpen] = useState(false);
  const [localSearch, setLocalSearch] = useState('');
  const triggerRef = useRef<HTMLDivElement>(null);
  const [dropdownWidth, setDropdownWidth] = useState<number | undefined>();
  const searchValue = onSearch ? optionsSearch : localSearch;
  const shouldShowSearch = showSearch ?? options.length > 10;

  const updateSearch = useCallback(
    (value: string) => {
      if (onSearch) {
        onSearch(value);
      } else {
        setLocalSearch(value);
      }
    },
    [onSearch]
  );

  const clearSearch = useCallback(() => {
    updateSearch('');
  }, [updateSearch]);

  const setDropdownOpen = useCallback(
    (visible: boolean) => {
      if (disabled) return;
      setOpen(visible);
      if (visible && triggerRef.current) {
        setDropdownWidth(triggerRef.current.clientWidth);
      }
      if (!visible && searchValue) {
        clearSearch();
      }
    },
    [clearSearch, disabled, searchValue]
  );

  const toggleOpen = useCallback(() => {
    setDropdownOpen(!open);
  }, [open, setDropdownOpen]);

  const availableTeams = useMemo(
    () =>
      onSearch
        ? options
        : options.filter((team) =>
            team.name.toLowerCase().includes(searchValue.trim().toLowerCase())
          ),
    [onSearch, searchValue, options]
  );

  const isTeamDisabled = (team: PreviewDropdownOptions) => {
    const hasPermission =
      formState === 'add'
        ? team?.permissions?.is_create
        : formState === 'edit'
          ? team?.permissions?.is_update
          : true;

    return team.disabled || !hasPermission;
  };

  const handleSelect = (team: PreviewDropdownOptions) => {
    if (isTeamDisabled(team)) return;
    const isSelected = selectedOptions.some(
      (selected) => selected.id === team.id
    );

    if (isSelected) {
      onChange(selectedOptions.filter((selected) => selected.id !== team.id));
      return;
    }

    onChange(singleSelect ? [team] : [...selectedOptions, team]);
    if (singleSelect) {
      setDropdownOpen(false);
    }
  };

  const handleRemove = (teamId: string, e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (disabled) return;
    onChange(selectedOptions.filter((team) => team.id !== teamId));
  };

  const handleClearAll = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (disabled) return;
    onChange([]);
  };

  const menu = (
    <div
      className='team-dropdown-select-menu'
      style={{ width: dropdownWidth ? `${dropdownWidth}px` : '100%' }}
      onClick={(e) => e.stopPropagation()}
    >
      {shouldShowSearch && (
        <>
          <div className='team-dropdown-select-search-row'>
            <SearchInput
              placeholder={'Search'}
              onChange={updateSearch}
              onClear={clearSearch}
              defaultValue={searchValue}
              width='100%'
              style={{ height: 28 }}
            />
          </div>
          <div className='team-dropdown-select-divider' />
        </>
      )}

      <div className='team-dropdown-select-options' onScroll={onPopupScroll}>
        <div className='team-dropdown-select-options-inner'>
          {availableTeams.length > 0 ? (
            availableTeams.map((team) => {
              const isSelected = selectedOptions.some(
                (selected) => selected.id === team.id
              );
              const isDisabled = isTeamDisabled(team) || team.is_default_admin;
              //this is a case when we are using this dropdwon in  ViewasUserBanner component
              //keep the option disabled if it has is_default_admin set to true, then dont let the option to select
              const tooltipTitle = team.is_default_admin
                ? 'Admin session cannot be previewed'
                : undefined;

              const optionButton = (
                <button
                  type='button'
                  key={team.id}
                  data-testid={`preview-dropdown-option-${team.id}`}
                  className={`team-dropdown-select-option ${isSelected ? 'is-selected' : ''} ${
                    isDisabled ? 'is-disabled' : ''
                  }`.trim()}
                  onClick={() => handleSelect(team)}
                  disabled={isDisabled}
                >
                  {renderOption ? ( //passed in case of ViewAsUserBanner.tsx
                    renderOption(team, isSelected)
                  ) : (
                    <>
                      {!singleSelect && (
                        <>
                          <Checkbox
                            checked={isSelected}
                            disabled={isDisabled}
                            className='team-dropdown-select-checkbox checkbox-sm'
                            onChange={() => handleSelect(team)}
                            onClick={(e) => e.stopPropagation()}
                          />
                          <span
                            className='team-dropdown-select-dot'
                            style={{ backgroundColor: team.color || '#000' }}
                          />
                        </>
                      )}
                      <EllipsisTooltip
                        text={team.name}
                        className='team-dropdown-select-option-text'
                      />
                    </>
                  )}
                </button>
              );

              if (tooltipTitle) {
                return (
                  <Tooltip key={team.id} title={tooltipTitle} placement='top'>
                    <span
                      style={{
                        display: 'block',
                        width: '100%',
                        cursor: 'not-allowed',
                      }}
                    >
                      {optionButton}
                    </span>
                  </Tooltip>
                );
              }

              return optionButton;
            })
          ) : loading ? null : (
            <div className='team-dropdown-select-empty'>
              {searchValue ? 'No matches found' : `No data available`}
            </div>
          )}
          {loading && (
            <div className='team-dropdown-select-loading'>Loading...</div>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className={`team-dropdown-select ${className}`.trim()}>
      {label && <div className='team-dropdown-select-label'>{label}</div>}
      <Dropdown
        overlay={menu}
        trigger={['click']}
        visible={open}
        onVisibleChange={setDropdownOpen}
        disabled={disabled}
        overlayClassName={overlayClassName}
      >
        <div
          ref={triggerRef}
          onClick={toggleOpen}
          tabIndex={disabled ? -1 : 0}
          role='button'
          aria-expanded={open}
          data-testid='preview-dropdown-trigger'
          className={`team-dropdown-select-trigger ${disabled ? 'is-disabled' : ''} ${
            open ? 'is-open' : ''
          }`.trim()}
        >
          <div className='team-dropdown-select-values'>
            {selectedOptions.length === 0 ? (
              <span className='team-dropdown-select-placeholder'>
                {searchPlaceholder || 'Select...'}
              </span>
            ) : renderTrigger ? (
              renderTrigger(selectedOptions)
            ) : singleSelect ? (
              <EllipsisTooltip
                text={selectedOptions[0]?.name || ''}
                className='team-dropdown-select-single-value'
              />
            ) : (
              selectedOptions.map((team) => (
                <TeamTagPill
                  key={team.id}
                  name={team.name}
                  color={team.color}
                  removable={!disabled}
                  onRemove={(e) => handleRemove(team.id, e)}
                />
              ))
            )}
          </div>
          <div className='team-dropdown-select-actions'>
            {selectedOptions.length > 0 && !disabled && showClearIcon && (
              <button
                type='button'
                className='team-dropdown-select-action-button'
                onClick={handleClearAll}
                aria-label='Clear selected options'
              >
                <CloseCircleFilled className='team-dropdown-select-clear' />
              </button>
            )}
            {!hideChevronWhenDisabled && (
              <span className='team-dropdown-select-action-button'>
                <ChevronDownSmallIcon
                  size={16}
                  color='#5E5E5E'
                  className='team-dropdown-select-chevron'
                />
              </span>
            )}
          </div>
        </div>
      </Dropdown>
    </div>
  );
};

export default PreviewDropdown;
